"use client";

/**
 * Delegate: login page under OIDC_ONLY (docs/delegate/PLAN.md, workstream A.1). One
 * "Continue with Delegate" button that starts the SysOp OIDC flow, auto-started on load.
 * No password form, no social buttons, no signup link.
 *
 * Inside the V2 shell's frame the SysOp login cannot work (its session cookie is SameSite=Lax on
 * another site, so the frame would land on the portal login). Framed, nothing starts on its own:
 * the button is a link that takes the top window to `/api/oauth/sysop/start`, which signs in
 * top-level and returns to the shell root (embedded-app SSO contract v1).
 */
import { APP_NAME } from "@calcom/lib/constants";
import { useCompatSearchParams } from "@calcom/lib/hooks/useCompatSearchParams";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Button } from "@coss/ui/components/button";
import { signIn } from "next-auth/react";
import { useEffect, useRef, useState } from "react";

export const SYSOP_PROVIDER_ID = "sysop";
export const FRAMED_SIGN_IN_HREF = "/api/oauth/sysop/start";

const isFramed = (): boolean => {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
};

export function DelegateSysopLogin({ callbackUrl }: { callbackUrl: string }) {
  const { t } = useLocale();
  const searchParams = useCompatSearchParams();
  const [starting, setStarting] = useState(false);
  const started = useRef(false);
  // null until mounted: the server render cannot know whether the page is framed.
  const [framed, setFramed] = useState<boolean | null>(null);

  const start = () => {
    started.current = true;
    setStarting(true);
    void signIn(SYSOP_PROVIDER_ID, { callbackUrl: callbackUrl || undefined });
  };

  // An `error` query means a SysOp round trip just failed; let the member retry by hand.
  const hasError = !!searchParams?.get("error");
  useEffect(() => setFramed(isFramed()), []);
  useEffect(() => {
    if (framed === false && !started.current && !hasError) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framed, hasError]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-default px-4 py-10">
      <div className="w-full max-w-md rounded-xl border border-subtle bg-default p-10 shadow-sm">
        <h1 className="mb-2 text-center font-cal text-xl font-bold text-emphasis">{APP_NAME}</h1>
        <p className="mb-8 text-center text-sm text-subtle" data-testid="login-subtitle">
          {t("welcome_back_sign_in")}
        </p>
        {framed ? (
          <Button
            className="w-full"
            data-testid="delegate-sysop-login"
            render={<a href={FRAMED_SIGN_IN_HREF} target="_top" rel="noopener" />}>
            {t("continue_with", { appName: "Delegate" })}
          </Button>
        ) : (
          <Button
            className="w-full"
            data-testid="delegate-sysop-login"
            disabled={starting || framed === null}
            onClick={(e) => {
              e.preventDefault();
              start();
            }}>
            {t("continue_with", { appName: "Delegate" })}
          </Button>
        )}
      </div>
    </div>
  );
}

export default DelegateSysopLogin;
